import React, { useEffect, useState, useRef, useCallback } from 'react'
import { createFormBag, FormBag, FormData, FormErrors, validateFormBag } from 'react-formage'

import { ButtonGroup } from 'components/ui/button'
import { FooterEditEntity } from 'components/ui/footer'
import { Input } from 'components/ui/input'
import { PrimaryNavigationEditEntity } from 'components/ui/primary-navigation/edit-entity'
import { PopOverMenu, PopOverMenuButton } from 'components/ui/pop-over-menu'
import { SecondaryNavigation } from 'components/ui/secondary-navigation'
import { StatusGuard } from 'components/ui/status-guard'
import { StatusSummary } from 'components/ui/status-summary'

import { sharedActionCreators } from 'containers/shared'
import { connect } from 'containers/store'

import { releaseControlsBindableActionCreators } from 'domains/release-controls/actions'
import { ReleaseControlUpdate } from 'domains/release-controls/components/release-control-update'
import { Media } from 'generated/mos/media'
import { spacesBindableActionCreators } from 'domains/spaces/actions'

import { Page } from 'generated/mos/pagination'
import { StopPointOfInterest, Mood } from 'generated/mos/org/getty/audio'
import { GetStopPointOfInterestRequest, ListVisitorTypesRequest } from 'generated/mos/org/getty/audioaccess'
import { UpdateStopPointOfInterestRequest } from 'generated/mos/org/getty/audiomanagement'
import { SetReleaseStatusRequest } from 'generated/mos/releasecontrolsmanagement'
import { Space, Site } from 'generated/mos/structure'
import { Placement, PositionPlacement, SitePlacement, SpacePlacement } from 'generated/mos/placement'
import { ListValidationFailuresRequest } from 'generated/mos/org/getty/validationaccess'
import { getValidationWarning } from 'domains/org/getty/utils/validation'
import { MediaField } from 'domains/media/components/media-field'

import { getResolvedValue } from 'helpers/linkables'
import { pick, areObjectsEqual, areRefValuesEqual } from 'helpers/core'
import { convertEntityToFormValues, writeUsingSchema } from 'helpers/schema'
import { Status, statusSelector } from 'helpers/status'
import { baseDeepLinkDomain, gettyCenterSiteId, gettyVillaSiteId } from 'helpers/deep-links'

import { LayoutListOrFormPage } from 'layouts/list-or-form-page'

import { actionCreators, GettyActionCreators } from '../../../actions'
import { GettyStore } from '../../../store'
import { SpacesStore } from '../../../../../spaces/store'
import { getPointOfInterestSubnav } from '../subnav'
import { PointOfInterestDelete } from '../point-of-interest-delete'
import { pointOfInterestSchema } from '../../../schemas/point-of-interest'
import { stopSchema } from '../../../schemas/stop'
import { TmsDetails } from 'domains/org/getty/components/tms-metadata'
import { visitorTypeHelpText } from '../../visitor-types/helper'

type FormValues = {
  teaserText: string | undefined;
  content: string | undefined;
  additionalInfo: string | undefined;
  deepDiveLabel: string | undefined;
  deepDiveContextLabel: string | undefined;
  deepDiveUrl: string | undefined;
  tags: string | undefined;
  headerText: string | undefined;
};

type ActionProps =
  Pick<GettyActionCreators, 'gettyListVisitorTypesRequest' | 'gettyGetStopPointOfInterestRequest' | 'gettyUpdateStopPointOfInterestRequest' | 'gettyUpdateStopPointOfInterestReset' | 'gettyListValidationFailuresRequest' | 'gettyListSitesRequest'> &
  Pick<typeof releaseControlsBindableActionCreators, "releaseControlsListReleaseControlsRequest" | "releaseControlsSetReleaseStatusRequest"> &
  Pick<typeof spacesBindableActionCreators, "spacesRequest"> &
  Pick<typeof sharedActionCreators, 'toastNotification'>;

type ConnectedProps = Pick<GettyStore['getty'], 'stopPointOfInterestUpdate' | 'sites' | 'validationFailuresList' | 'visitorTypes'> & Pick<SpacesStore["spaces"], "spacesList">


type DirectProps = {
  readonly historyPush: (id: string) => void;
  readonly projectId: string;
  readonly pointOfInterestId: string;
  readonly moodId: string;
};
type Props = ActionProps & ConnectedProps & DirectProps;

export const PointOfInterestUpdateView = (props: Props) => {
  const {
    gettyGetStopPointOfInterestRequest,
    gettyListSitesRequest,
    gettyUpdateStopPointOfInterestRequest,
    gettyUpdateStopPointOfInterestReset,
    historyPush,
    projectId,
    pointOfInterestId,
    stopPointOfInterestUpdate,
    toastNotification,
    moodId,
    validationFailuresList,
    spacesList,
    gettyListValidationFailuresRequest,
    spacesRequest,
    releaseControlsListReleaseControlsRequest,
    releaseControlsSetReleaseStatusRequest,
    sites,
    visitorTypes,
  } = props

  // setup form value variables.
  const [ formBag, setFormBag ] = useState<FormBag<FormValues> | undefined>(undefined)
  const [ audioMedia, setAudioMedia ] = useState<Media.Ref | undefined>(undefined)

  const hasSubmitted = useRef(false)

  const [ deletePointOfInterestModalOpen, setDeletePointOfInterestModalOpen ] = useState(false)

  // where to return to if the user wants to navigate back.
  // if we came here from within a tour, go back to that tour, otherwise go to all pointsOfInterest.
  const backLocation = `/project/${projectId}/mood/${moodId}`

  const pointOfInterestData = statusSelector.data(stopPointOfInterestUpdate)
  const validationFailuresListData = statusSelector.data(validationFailuresList)
  const visitorTypesData = statusSelector.data(visitorTypes)
  const pointOfInterest = pointOfInterestData ? pointOfInterestData.stopPointOfInterest : undefined
  const [moods, setMoods] = useState(pointOfInterestData ? pointOfInterestData.moods : undefined)
  const [tours, setTours] = useState(pointOfInterestData ? pointOfInterestData.tours : undefined)
  const pointOfInterestRef: StopPointOfInterest.Ref = {
    typename: StopPointOfInterest.refName,
    id: pointOfInterestId,
  }
  const [siteId, setSiteId] = useState('');

  const [tmsObject, setTmsObject] = useState(pointOfInterestData && pointOfInterestData.baseEntity ? pointOfInterestData.baseEntity : undefined);

  if (pointOfInterestData && pointOfInterestData.baseEntity && tmsObject == undefined) {
    setTmsObject(pointOfInterestData.baseEntity)
  }

  if (pointOfInterestData && pointOfInterestData.moods && moods == undefined) {
    setMoods(pointOfInterestData.moods)
  }

  if (pointOfInterestData && pointOfInterestData.tours && tours == undefined) {
    setTours(pointOfInterestData.tours)
  }


  // load initial pointOfInterest data.
  useEffect(() => {
    if (stopPointOfInterestUpdate.status === Status.Idle || (pointOfInterest && StopPointOfInterest.mustRef(pointOfInterest.ref).id !== pointOfInterestId)) {
      gettyGetStopPointOfInterestRequest({
        type: GetStopPointOfInterestRequest.refName,
        stopPointOfInterestRef: pointOfInterestRef
      })
      gettyListSitesRequest()
      StopPointOfInterest.refName
      hasSubmitted.current = false
    }

    if (spacesList.status === Status.Idle) {
      spacesRequest()
    }

    releaseControlsListReleaseControlsRequest([pointOfInterestRef])

    gettyListValidationFailuresRequest({
      ...ListValidationFailuresRequest.defaults,
      page: {
        ...Page.defaults,
        pageSize: 100,
      },
      filter: {
        ...ListValidationFailuresRequest.ByRef.defaults,
        entityRefs: [pointOfInterestRef]
      }
    })
  }, []) // eslint-disable-line react-hooks/exhaustive-deps


  const getResolvedFormValues = (keys: ReadonlyArray<keyof StopPointOfInterest.Entity>): { [key: string]: any} => {
    const pointOfInterestValues: Partial<StopPointOfInterest.Entity> = pick({
      ...StopPointOfInterest.defaults,
      ...pointOfInterest
    }, ...keys)
    return convertEntityToFormValues<StopPointOfInterest.Entity>(pointOfInterestValues, pointOfInterestSchema)
  }

  // setup the initial form values.
  const initialFormValues: FormValues | undefined = pointOfInterest ?
    {
      ...(getResolvedFormValues(['teaserText', 'content', 'additionalInfo', 'deepDiveLabel', 'deepDiveContextLabel', 'deepDiveUrl', 'tags', 'headerText']) as FormValues),
    }:
    undefined
  const initialAudioValue = pointOfInterest && pointOfInterestSchema.audio.read(pointOfInterest.audio) as Media.Ref | undefined;

  const deepLinkUrl = `${baseDeepLinkDomain}?deeplink=site/${siteId}/mood/${moodId}/point-of-interest/${pointOfInterestId}`

  useEffect(() => {
    // once the entity is ready, initialise the form values, or reinitialise once an update was
    // successful.
    if (stopPointOfInterestUpdate.status === Status.Ready && initialFormValues) {
      setFormBag(createFormBag(initialFormValues))
      setAudioMedia(initialAudioValue)
    }

    // if the status is ready and the data had been submitted, trigger success toast.
    if (stopPointOfInterestUpdate.status === Status.Ready && hasSubmitted.current) {
      releaseControlsListReleaseControlsRequest([pointOfInterestRef])
      toastNotification({ type: 'success', text: 'Point Of Interest updated' })
      hasSubmitted.current = false
      gettyListValidationFailuresRequest({
        ...ListValidationFailuresRequest.defaults,
        page: {
          ...Page.defaults,
          pageSize: 100,
        },
        filter: {
          ...ListValidationFailuresRequest.ByRef.defaults,
          entityRefs: [pointOfInterestRef]
        }
      })
    }

    // if the status is stale, trigger failure toast.
    if (stopPointOfInterestUpdate.status === Status.Stale) {
      gettyUpdateStopPointOfInterestReset()
      toastNotification({ type: 'error', text: 'Can’t save because of an unknown error.' })
      hasSubmitted.current = false
    }
  }, [ stopPointOfInterestUpdate.status ]) // eslint-disable-line react-hooks/exhaustive-deps

  useEffect(() => {
    const mood = moods && moods.find(obj => obj && obj.ref && obj.ref.id === moodId);
    setSiteId(mood && mood.siteRef && mood.siteRef.id || '')

  }, [moodId, moods]); // eslint-disable-line react-hooks/exhaustive-deps


  // handle the form ui.
  const modified = initialFormValues && pointOfInterestData && formBag && (
    !areObjectsEqual(initialFormValues, formBag.values) ||
    !areRefValuesEqual(initialAudioValue, audioMedia)
  )

  const validateForm = (values: FormValues) => {
    const errors: FormErrors<FormValues> = {}

    return errors
  }

  // attempt to submit validated data to the api.
  const submit = (formBag: FormBag<FormValues>, pointOfInterest: StopPointOfInterest.Entity) => {
    const bag = validateFormBag(formBag, validateForm)
    setFormBag(bag)

    if (!bag.valid) {
      return false
    }

    hasSubmitted.current = true
    const values: FormValues = formBag.values;
    const entityValues = Object.entries(values).reduce((accumulator, currentValue) => {
      const key = currentValue[0] as keyof StopPointOfInterest.Entity
      const value = currentValue[1]
      return {
        ...accumulator,
        [key]: pointOfInterestSchema[key] ? pointOfInterestSchema[key].write(value, pointOfInterest[key]) : value
      }
    }, {})

    gettyUpdateStopPointOfInterestRequest({
      ...UpdateStopPointOfInterestRequest.defaults,
      stopPointOfInterestRef: StopPointOfInterest.mustRef(pointOfInterest.ref),
      updateMask: {
        type: 'google.protobuf.FieldMask',
        fields: [
          'teaserText',
          'content',
          'additionalInfo',
          'deepDiveLabel',
          'deepDiveContextLabel',
          'deepDiveUrl',
          'tags',
          'audio',
          'headerText',
        ] as any,
      },
      ...entityValues,
      audio: writeUsingSchema<StopPointOfInterest.Entity>(pointOfInterestSchema, 'audio', audioMedia, pointOfInterest.audio),
    })

    return false
  }

  const releaseControlUpdate = (bundles: ReadonlyArray<SetReleaseStatusRequest.BundleStatus.Entity>) => {
    releaseControlsSetReleaseStatusRequest({
      ...SetReleaseStatusRequest.defaults,
      ref: pointOfInterestRef,
      bundles,
    })
  }

  const failures = validationFailuresListData ? validationFailuresListData.find(({ ref }) => ref && ref.id === pointOfInterestId) : undefined
  const failureFields = failures && failures.issues

  let title: any = '';
  if (pointOfInterest && pointOfInterest.stop) {
    title = getResolvedValue(
      stopSchema.title.read(pointOfInterest.stop.title),
      statusSelector.hasData(stopPointOfInterestUpdate) ? stopPointOfInterestUpdate.data.resolvedValues : []
    )
  }

  return (
    <>
      {deletePointOfInterestModalOpen && (
        <PointOfInterestDelete
          historyPush={historyPush}
          projectId={projectId}
          onCancel={() => setDeletePointOfInterestModalOpen(false)}
          pointOfInterest={pointOfInterest}
          moodId={moodId}
        />
      )}
      <LayoutListOrFormPage
        nav={() => (
          <PrimaryNavigationEditEntity
            title="Edit Point of Interest"
            onConfirm={modified ? () => historyPush(backLocation) : undefined}
            navBackRoute={backLocation}
          />
        )}
        title={title}
        toolbar={() => (
          <ButtonGroup>
            <ReleaseControlUpdate
              hasBorder
              entityRef={pointOfInterestRef}
              updateHandler={releaseControlUpdate}
            />
            <PopOverMenu hasBorder>
              <PopOverMenuButton onClick={() => setDeletePointOfInterestModalOpen(true)}>
                Delete
              </PopOverMenuButton>
            </PopOverMenu>
          </ButtonGroup>
        )}
        summary={failureFields ? () => failureFields && <StatusSummary warning={[{ text: `The following fields are required to publish this point of interest: ${failureFields.map(({ attributeDisplayName }) => attributeDisplayName).join(', ') }.` }]} /> : undefined}
        subnav={() => (
          <SecondaryNavigation
            navItems={getPointOfInterestSubnav(projectId, pointOfInterestId, moodId)}
            modified={modified}
            historyPush={historyPush}
          />
        )}
        content={() => (
          <StatusGuard status={stopPointOfInterestUpdate} mode="form">
            {formBag && pointOfInterest && (
            <form noValidate onSubmit={(event) => { event.preventDefault() }}>
              <FormData bag={formBag} onUpdate={(event) => { setFormBag(event.bag) }} validate={() => ({})} >
                <Input
                  component="textarea"
                  label="Teaser Text"
                  field="teaserText"
                  warning={getValidationWarning<StopPointOfInterest.Entity>('teaserText', failureFields)}
                />
                <Input
                  component="textarea"
                  label="Content"
                  field="content"
                  warning={getValidationWarning<StopPointOfInterest.Entity>('content', failureFields)}
                />
                <Input
                  component="textarea"
                  label="Header Text"
                  field="headerText"
                  warning={getValidationWarning<StopPointOfInterest.Entity>('headerText', failureFields)}
                />
                <Input
                  component="textarea"
                  label="Additional Info"
                  field="additionalInfo"
                  warning={getValidationWarning<StopPointOfInterest.Entity>('additionalInfo', failureFields)}
                />
                <Input
                  label="Deep Dive Label"
                  field="deepDiveLabel"
                  warning={getValidationWarning<StopPointOfInterest.Entity>('deepDiveLabel', failureFields)}
                />
                <Input
                  label="Deep Dive Context Label"
                  field="deepDiveContextLabel"
                  warning={getValidationWarning<StopPointOfInterest.Entity>('deepDiveContextLabel', failureFields)}
                />
                <Input
                  label="Deep Dive URL"
                  field="deepDiveUrl"
                  warning={getValidationWarning<StopPointOfInterest.Entity>('deepDiveUrl', failureFields)}
                />
                <MediaField
                  kind="MEDIA_KIND_AUDIO"
                  mediaRef={audioMedia}
                  onUpdate={(entity) => { setAudioMedia(entity ? entity.ref : undefined) }}
                  label="Audio"
                  warning={getValidationWarning<StopPointOfInterest.Entity>('audio', failureFields)}
                  />
                <Input
                  label="Visitor Type Tags"
                  field="tags"
                  warning={getValidationWarning<StopPointOfInterest.Entity>('tags', failureFields) || visitorTypeHelpText(visitorTypesData)}
                />
              </FormData>
            </form>
              )}
            <section>
              <h4>Deep Link</h4>
              <a href={deepLinkUrl} target="_blank" rel="noopener noreferrer">
                {deepLinkUrl}
              </a>
            </section>
          </StatusGuard>
        )}
        sidebar={() => (
          <TmsDetails
            tmsObject={tmsObject}
            sites={sites}
            spacesListData={statusSelector.data(spacesList)}
            stop={pointOfInterest && pointOfInterest.stop}
            tours={tours}
            moods={moods}
          />
        )}
        footer={() => formBag && pointOfInterest && (
          <FooterEditEntity
            entity={stopPointOfInterestUpdate}
            modified={modified}
            onCancel={() => historyPush(backLocation)}
            onSave={() => {
              submit(formBag, pointOfInterest)
              return false
            }}
          />
        )}
      />
    </>
  )
}

export const PointOfInterestUpdate = connect<ConnectedProps, ActionProps, DirectProps, GettyStore, GettyActionCreators>(
  (store) => ({
    ...pick(store.getty, "stopPointOfInterestUpdate", "sites", "validationFailuresList", "visitorTypes"),
    ...pick(store.spaces, "spacesList"),
  }),
  {
    ...pick(actionCreators, 'gettyGetStopPointOfInterestRequest', 'gettyUpdateStopPointOfInterestRequest', 'gettyUpdateStopPointOfInterestReset', 'gettyListSitesRequest', 'gettyListValidationFailuresRequest', 'gettyListVisitorTypesRequest'),
    ...pick(releaseControlsBindableActionCreators, 'releaseControlsListReleaseControlsRequest', 'releaseControlsSetReleaseStatusRequest'),
    ...pick(spacesBindableActionCreators, "spacesRequest"),
    ...pick(sharedActionCreators, 'toastNotification'),
  }
)(PointOfInterestUpdateView)
